import { AUTH_TOKEN_COOKIE_NAME } from './constants/cookies';
import {
  getUniversalCookie,
  removeUniversalCookie,
  setUniversalCookie,
} from './cookies';

import type { NextIncomingMessage } from './types/request';

/**
 * `auth_token` is the token itself on some responses and a boolean "this
 * session has a token" flag on others, and the call sites cast rather than
 * check. Coercing the flag writes the literal cookie value "true", which then
 * rides on every X-Auth-Token header - the API answers "No user found" and the
 * session looks signed in while nothing authenticated works (DEV-6633).
 */
const isAuthToken = (value: unknown): value is string =>
  typeof value === 'string' &&
  value !== '' &&
  value !== 'true' &&
  value !== 'false';

export const getAuthToken = (req?: NextIncomingMessage) => {
  const options: { req?: NextIncomingMessage } = {};

  if (req) {
    options.req = req;
  }

  const token = getUniversalCookie<string>(AUTH_TOKEN_COOKIE_NAME, options);

  if (isAuthToken(token)) {
    return token;
  }

  // Clear a poisoned cookie rather than just ignoring it. Nothing else will:
  // the v1 API reports auth failure as HTTP 200 with `success: false`, so the
  // 401 teardown in useHandleAuthError never fires, and the value has a
  // 180-day expiry.
  if (token !== undefined) {
    removeAuthToken(req);
  }

  return;
};

export const setAuthToken = (value: string, req?: NextIncomingMessage) => {
  if (!isAuthToken(value)) {
    return;
  }

  const options: { req?: NextIncomingMessage; options?: { maxAge: number } } = {
    options: {
      maxAge: 180 * 24 * 60 * 60, // 180 days, in seconds
    },
  };

  if (req) {
    options.req = req;
  }

  return setUniversalCookie(AUTH_TOKEN_COOKIE_NAME, value, options);
};

export const removeAuthToken = (req?: NextIncomingMessage) => {
  const options: { req?: NextIncomingMessage } = {};

  if (req) {
    options.req = req;
  }

  return removeUniversalCookie(AUTH_TOKEN_COOKIE_NAME, options);
};
